// Vendor Registration

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { formatPhoneInput } from '../utils/formatters';
import VendorTypeForm, { buildInitialValues, buildSavePayload } from '../components/VendorTypeForm';
import { officiantsSchema } from '../schemas/officiants';
import { floristsSchema } from '../schemas/florists';
import { plannersSchema } from '../schemas/planners';
import { photographySchema } from '../schemas/photography';
import { djBandMusicianSchema } from '../schemas/djBandMusician';
import { cakesSchema } from '../schemas/cakes';
import { invitationsSchema } from '../schemas/invitations';
import { photoBoothsSchema } from '../schemas/photoBooths';
import { rentalsSchema } from '../schemas/rentals';
import { transportationSchema } from '../schemas/transportation';
import { weddingAttireSchema } from '../schemas/weddingAttire';
import { caterersSchema } from '../schemas/caterers';
import { venueSchema } from '../schemas/venue';
import { hairMakeupSchema } from '../schemas/hairMakeup';
import { validateSchema } from '../utils/validation';
import { US_STATES, ALL_LANGUAGES, DIVERSITY_TAGS, ALL_VENDOR_TYPES } from '../constants/vendorOptions';
import { hasServiceAreaGap } from '../utils/serviceArea';
import { SHOW_INSURANCE_FEATURE } from '../constants/featureFlags';
import ImageCropModal, { TARGET_ASPECT_RATIO } from '../components/ImageCropModal';
import { getCountyForCity, getCountyForCityState, getCountiesWithinRadius } from '../utils/countyLookup';
import CityAutocomplete from '../components/CityAutocomplete';
import MediaRightsConfirmationModal from '../components/MediaRightsConfirmationModal';
import { logMediaRightsConfirmation } from '../utils/mediaRightsLogger';

const HEARD_ABOUT_OPTIONS = [
  'Instagram', 'TikTok', 'Facebook', 'Pinterest', 'Google/Search',
  'Friend or family', 'Wedding vendor', 'Wedding venue',
  'Wedding planner/coordinator', 'Bridal show/wedding event',
  'Advertisement', 'Other',
];

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const RADIUS_OPTIONS = [25, 50, 75, 100];

const STEP_LABELS = ['Account', 'Business Info', 'Service Area', 'Details', 'Media'];

export default function VendorRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Step 0 - Account
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [vendorType, setVendorType] = useState(searchParams.get('type') || '');
  const [heardAboutUs, setHeardAboutUs] = useState('');
  const [heardAboutUsOther, setHeardAboutUsOther] = useState('');

  // Step 1 - Business Info
  const [phone, setPhone] = useState('');
  const [phonePublic, setPhonePublic] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactNamePublic, setContactNamePublic] = useState(false);
  const [website, setWebsite] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [briefIntro, setBriefIntro] = useState('');
  const [bio, setBio] = useState('');
  const [diff1, setDiff1] = useState('');
  const [diff2, setDiff2] = useState('');
  const [diff3, setDiff3] = useState('');
  const [hasInsurance, setHasInsurance] = useState(false);
  const [experienceYear, setExperienceYear] = useState('');
  const [languages, setLanguages] = useState([]);
  const [otherLanguage, setOtherLanguage] = useState('');
  const [diversityTags, setDiversityTags] = useState([]);
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [pinterest, setPinterest] = useState('');

  // Step 2 - Service Area
  const [serviceRadius, setServiceRadius] = useState(25);
  const [customRadius, setCustomRadius] = useState('');
  const [useCustomRadius, setUseCustomRadius] = useState(false);
  const [additionalCities, setAdditionalCities] = useState([]);
  const [newCity, setNewCity] = useState('');
  const [mapCoords, setMapCoords] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeFailed, setGeocodeFailed] = useState(false);
  const [cityMarkers, setCityMarkers] = useState([]);

  // Step 3 - Vendor Specific (Photography/Videography)
  const [photographyValues, setPhotographyValues] = useState(() => buildInitialValues(photographySchema, null));
  const handlePhotographyChange = (key, newValue) => setPhotographyValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Venue)
  const [venueValues, setVenueValues] = useState(() => buildInitialValues(venueSchema, null));
  const handleVenueChange = (key, newValue) => setVenueValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Photo Booths)
  const [photoBoothsValues, setPhotoBoothsValues] = useState(() => buildInitialValues(photoBoothsSchema, null));
  const handlePhotoBoothsChange = (key, newValue) => setPhotoBoothsValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Cakes & Bakeries)
  const [cakesValues, setCakesValues] = useState(() => buildInitialValues(cakesSchema, null));
  const handleCakesChange = (key, newValue) => setCakesValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Planners)
  const [plannerValues, setPlannerValues] = useState(() => buildInitialValues(plannersSchema, null));
  const handlePlannerChange = (key, newValue) => setPlannerValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Caterers)
  const [caterersValues, setCaterersValues] = useState(() => buildInitialValues(caterersSchema, null));
  const handleCaterersChange = (key, newValue) => setCaterersValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Hair & Makeup)
  const [hairMakeupValues, setHairMakeupValues] = useState(() => buildInitialValues(hairMakeupSchema, null));
  const handleHairMakeupChange = (key, newValue) => setHairMakeupValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (DJs/Bands/Musicians)
  const [djBandValues, setDjBandValues] = useState(() => buildInitialValues(djBandMusicianSchema, null));
  const handleDjBandChange = (key, newValue) => setDjBandValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Florists)
  const [floristValues, setFloristValues] = useState(() => buildInitialValues(floristsSchema, null));
  const handleFloristChange = (key, newValue) => setFloristValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Officiants)
  const [officiantValues, setOfficiantValues] = useState(() => buildInitialValues(officiantsSchema, null));
  const handleOfficiantChange = (key, newValue) => setOfficiantValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Wedding Attire)
  const [weddingAttireValues, setWeddingAttireValues] = useState(() => buildInitialValues(weddingAttireSchema, null));
  const handleWeddingAttireChange = (key, newValue) => setWeddingAttireValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Transportation)
  const [transportationValues, setTransportationValues] = useState(() => buildInitialValues(transportationSchema, null));
  const handleTransportationChange = (key, newValue) => setTransportationValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Rentals/Décor/Lighting)
  const [rentalsValues, setRentalsValues] = useState(() => buildInitialValues(rentalsSchema, null));
  const handleRentalsChange = (key, newValue) => setRentalsValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 3 - Vendor Specific (Invitations & Stationery)
  const [invitationsValues, setInvitationsValues] = useState(() => buildInitialValues(invitationsSchema, null));
  const handleInvitationsChange = (key, newValue) => setInvitationsValues((prev) => ({ ...prev, [key]: newValue }));

  // Step 4 - Media
  const [reelFile, setReelFile] = useState(null);
  const [mediaRightsConfirmed, setMediaRightsConfirmed] = useState(false);
  const [showMediaRightsModal, setShowMediaRightsModal] = useState(false);
  const [reelPreview, setReelPreview] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState('');

  const validateStep0 = () => {
    if (!email.trim() || !password || !confirmPassword || !companyName.trim() || !vendorType || !heardAboutUs) {
      setError('Please fill in all fields.');
      return false;
    }
    if (heardAboutUs === 'Other' && !heardAboutUsOther.trim()) {
      setError('Please specify how you heard about us.');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep1 = () => {
    if (!phone.trim() || !contactName.trim() || !addressLine1.trim() || !city.trim() || !state || !zip.trim()) {
      setError('Please fill in all required fields.');
      return false;
    }
    if (phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return false;
    }
    if (!diff1.trim() || !diff2.trim() || !diff3.trim()) {
      setError('Please fill in all 3 differentiators.');
      return false;
    }
    if (!briefIntro.trim()) {
      setError('Brief intro is required.');
      return false;
    }
    if (!bio.trim()) {
      setError('Bio is required.');
      return false;
    }
    setError('');
    return true;
  };

  const addCity = async () => {
    const trimmed = newCity.trim();
    if (trimmed && !additionalCities.includes(trimmed)) {
      setAdditionalCities([...additionalCities, trimmed]);
      setNewCity('');
      const coords = await geocodeCity(trimmed);
      if (coords) {
        setCityMarkers((prev) => [...prev, coords]);
      }
    }
  };

  const removeCity = (cityToRemove) => {
    setAdditionalCities(additionalCities.filter((c) => c !== cityToRemove));
    setCityMarkers(cityMarkers.filter((m) => m.name !== cityToRemove));
  };

  const getZoomForRadius = (miles) => {
    if (miles <= 10) return 9.5;
    if (miles <= 25) return 8.5;
    if (miles <= 50) return 8;
    if (miles <= 75) return 7.5;
    if (miles <= 100) return 7;
    return 6.5;
  };

  const geocodeAddress = async () => {
    const fullAddress = `${addressLine1.trim()}, ${city.trim()}, ${state} ${zip.trim()}`;
    setGeocoding(true);
    setGeocodeFailed(false);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fullAddress)}&format=json&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        setMapCoords(coords);
        setGeocoding(false);
        return coords;
      } else {
        setMapCoords(null);
        setGeocodeFailed(true);
        setGeocoding(false);
        return null;
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      setMapCoords(null);
      setGeocodeFailed(true);
      setGeocoding(false);
      return null;
    }
  };

  const geocodeCity = async (cityState) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityState)}&format=json&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), name: cityState };
      }
    } catch (err) {
      console.error('City geocoding error:', err);
    }
    return null;
  };

  const toggleInArray = (value, array, setArray) => {
    if (array.includes(value)) {
      setArray(array.filter((v) => v !== value));
    } else {
      setArray([...array, value]);
    }
  };

  const [imageCrops, setImageCrops] = useState([]);
  const [cropQueue, setCropQueue] = useState([]); // files waiting to be cropped
  const [croppingFile, setCroppingFile] = useState(null); // { file, previewUrl } currently shown in the modal

  const handleReelSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setReelFile(file);
    setReelPreview(URL.createObjectURL(file));
  };

  const handleImagesSelect = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 9 - imageFiles.length;
    const toAdd = files.slice(0, remaining);
    if (toAdd.length === 0) return;
    setCropQueue(toAdd);
    setCroppingFile({ file: toAdd[0], previewUrl: URL.createObjectURL(toAdd[0]) });
  };

  const handleCropConfirm = (cropData) => {
    setImageFiles((prev) => [...prev, croppingFile.file]);
    setImagePreviews((prev) => [...prev, croppingFile.previewUrl]);
    setImageCrops((prev) => [...prev, cropData]);

    const remainingQueue = cropQueue.slice(1);
    setCropQueue(remainingQueue);
    if (remainingQueue.length > 0) {
      setCroppingFile({ file: remainingQueue[0], previewUrl: URL.createObjectURL(remainingQueue[0]) });
    } else {
      setCroppingFile(null);
    }
  };

  const [editingCropIndex, setEditingCropIndex] = useState(null);

  const handleEditCropConfirm = (cropData) => {
    const index = editingCropIndex;
    setImageCrops((prev) => {
      const updated = [...prev];
      updated[index] = cropData;
      return updated;
    });
    setEditingCropIndex(null);
  };

  const handleCropCancel = () => {
    const remainingQueue = cropQueue.slice(1);
    setCropQueue(remainingQueue);
    if (remainingQueue.length > 0) {
      setCroppingFile({ file: remainingQueue[0], previewUrl: URL.createObjectURL(remainingQueue[0]) });
    } else {
      setCroppingFile(null);
    }
  };

  const removeImage = (index) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    setImageCrops(imageCrops.filter((_, i) => i !== index));
  };

  const validateStep3 = () => {
    if (vendorType === 'Photography' || vendorType === 'Videography') {
      const photographyErr = validateSchema(photographySchema, photographyValues);
      if (photographyErr) { setError(photographyErr); return false; }
    }
    if (vendorType === 'Venue') {
      const venueErr = validateSchema(venueSchema, venueValues);
      if (venueErr) { setError(venueErr); return false; }
    }
    if (vendorType === 'Photo Booths') {
      const photoBoothsErr = validateSchema(photoBoothsSchema, photoBoothsValues);
      if (photoBoothsErr) { setError(photoBoothsErr); return false; }
    }
    if (vendorType === 'Planners') {
      const plannerErr = validateSchema(plannersSchema, plannerValues); if (plannerErr) { setError(plannerErr); return false; }
    }
    if (vendorType === 'Caterers') {
      const caterersErr = validateSchema(caterersSchema, caterersValues);
      if (caterersErr) { setError(caterersErr); return false; }
    }
    if (vendorType === 'Hair & Makeup') {
      const hairMakeupErr = validateSchema(hairMakeupSchema, hairMakeupValues);
      if (hairMakeupErr) { setError(hairMakeupErr); return false; }
    }
    if (vendorType === 'DJs' || vendorType === 'Bands' || vendorType === 'Musicians') {
      const djBandErr = validateSchema(djBandMusicianSchema, djBandValues);
      if (djBandErr) { setError(djBandErr); return false; }
    }
    if (vendorType === 'Transportation') {
      const transportationErr = validateSchema(transportationSchema, transportationValues);
      if (transportationErr) { setError(transportationErr); return false; }
    }
    if (vendorType === 'Rentals/Décor/Lighting') {
      const rentalsErr = validateSchema(rentalsSchema, rentalsValues);
      if (rentalsErr) { setError(rentalsErr); return false; }
    }
    if (vendorType === 'Invitations & Stationery') {
      const invitationsErr = validateSchema(invitationsSchema, invitationsValues);
      if (invitationsErr) { setError(invitationsErr); return false; }
    }
    if (vendorType === 'Wedding Attire') {
      const weddingAttireErr = validateSchema(weddingAttireSchema, weddingAttireValues);
      if (weddingAttireErr) { setError(weddingAttireErr); return false; }
    }
    setError('');
    return true;
  };


  const validateStep4 = () => {
    if (!reelFile) {
      setError('An intro reel is required.');
      return false;
    }
    if (imageFiles.length === 0) {
      setError('At least 1 gallery image is required.');
      return false;
    }
    if (!mediaRightsConfirmed) {
      setError('You must confirm you have the rights to this content before continuing.');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (step === 0 && !validateStep0()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (step === 1 && !validateStep1()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (step === 3 && !validateStep3()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const nextStep = step + 1;
    setStep(nextStep);
    if (nextStep === 2) {
      geocodeAddress();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getVendorSpecificFields = () => {
    switch (vendorType) {
      case 'Photography':
      case 'Videography':
        return buildSavePayload(photographySchema, photographyValues);
      case 'Venue':
        return buildSavePayload(venueSchema, venueValues);
      case 'Photo Booths':
        return buildSavePayload(photoBoothsSchema, photoBoothsValues);
      case 'Cakes & Bakeries':
        return buildSavePayload(cakesSchema, cakesValues);
      case 'Planners':
        return buildSavePayload(plannersSchema, plannerValues);
      case 'Caterers':
        return buildSavePayload(caterersSchema, caterersValues);
      case 'Hair & Makeup':
        return buildSavePayload(hairMakeupSchema, hairMakeupValues);
      case 'DJs':
      case 'Bands':
      case 'Musicians':
        return buildSavePayload(djBandMusicianSchema, djBandValues);
      case 'Florists':
        return buildSavePayload(floristsSchema, floristValues);
      case 'Officiants':
        return buildSavePayload(officiantsSchema, officiantValues);
      case 'Wedding Attire':
        return buildSavePayload(weddingAttireSchema, weddingAttireValues);
      case 'Transportation':
        return buildSavePayload(transportationSchema, transportationValues);
      case 'Rentals/Décor/Lighting':
        return buildSavePayload(rentalsSchema, rentalsValues);
      case 'Invitations & Stationery':
        return buildSavePayload(invitationsSchema, invitationsValues);
      default:
        return {};
    }
  };

  const handleSubmit = async () => {
    if (!validateStep4()) return;
    setSubmitting(true);
    setError('');
    try {
      // mapCoords may still be null/stale here if the vendor advanced
      // through the wizard faster than the earlier background geocode
      // (triggered on step 2) finished. Fall back to a fresh, awaited
      // geocode so we never save null coordinates for a valid address
      // just because of a timing race.
      const finalCoords = mapCoords || (await geocodeAddress());

      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const uid = credential.user.uid;

      const storage = getStorage();
      setUploadProgress('Uploading reel...');
      const reelRef = ref(storage, `vendors/${uid}/reel/reel.mp4`);
      await uploadBytes(reelRef, reelFile);
      const reelUrl = await getDownloadURL(reelRef);

      const imageUrls = [];
      for (let i = 0; i < imageFiles.length; i++) {
        setUploadProgress(`Uploading image ${i + 1} of ${imageFiles.length}...`);
        const imgRef = ref(storage, `vendors/${uid}/gallery/image_${i}.jpg`);
        await uploadBytes(imgRef, imageFiles[i]);
        const url = await getDownloadURL(imgRef);
        const crop = imageCrops[i] || {};
        imageUrls.push({
          url,
          cropX: crop.cropX ?? 0,
          cropY: crop.cropY ?? 0,
          cropWidth: crop.cropWidth ?? null,
          cropHeight: crop.cropHeight ?? null,
        });
      }

      await logMediaRightsConfirmation({
        vendorId: uid,
        userId: uid,
        mediaIds: [reelUrl, ...imageUrls.map((img) => img.url)],
      });

      setUploadProgress('Saving profile...');

      const homeCounties = (await getCountyForCity(city.trim(), state)) || [];
      const additionalCountiesLists = await Promise.all(
        additionalCities.map((c) => getCountyForCityState(c))
      );
      const radiusMiles = useCustomRadius ? (parseInt(customRadius) || 0) : serviceRadius;
      const radiusCounties = finalCoords
        ? await getCountiesWithinRadius(finalCoords.lat, finalCoords.lng, radiusMiles)
        : [];
      // Combine the home address's county, every additional city's
      // county, and every county the service radius itself reaches
      // into one deduplicated list — this vendor is considered part of
      // every county they operate from, explicitly added, or can
      // realistically be found in via their travel radius.
      const allCounties = [
        ...homeCounties,
        ...additionalCountiesLists.filter(Boolean).flat(),
        ...radiusCounties,
      ];
      const counties = [...new Set(allCounties)];

      await setDoc(doc(db, 'vendors', uid), {
        uid,
        email: email.trim(),
        companyName: companyName.trim(),
        vendorTypes: [vendorType],
        heardAboutUs,
        heardAboutUsOther: heardAboutUs === 'Other' ? heardAboutUsOther.trim() : '',
        phone: phone.trim(),
        phonePublic,
        contactName: contactName.trim(),
        contactNamePublic,
        website: website.trim(),
        businessAddress: {
          line1: addressLine1.trim(),
          line2: addressLine2.trim(),
          city: city.trim(),
          state,
          zip: zip.trim(),
        },
        location: `${city.trim()}, ${state}`,
        latitude: finalCoords?.lat ?? null,
        longitude: finalCoords?.lng ?? null,
        counties: counties ?? null,
        briefIntro: briefIntro.trim(),
        bio: bio.trim(),
        differentiators: [diff1.trim(), diff2.trim(), diff3.trim()],
        hasInsurance,
        experienceYear: experienceYear ? parseInt(experienceYear) : 0,
        experienceYears: experienceYear ? (new Date().getFullYear() - parseInt(experienceYear)) : 0,
        languages: languages.includes('Other') && otherLanguage.trim()
          ? [...languages.filter((l) => l !== 'Other'), otherLanguage.trim()]
          : languages,
        diversityTags,
        instagram: instagram.trim(),
        facebook: facebook.trim(),
        tiktok: tiktok.trim(),
        pinterest: pinterest.trim(),
        serviceRadiusMiles: useCustomRadius ? (parseInt(customRadius) || 0) : serviceRadius,
        additionalCities,
        additionalCitiesCoords: cityMarkers.map((m) => ({ latitude: m.lat, longitude: m.lng })),
        serviceAreaMissing: hasServiceAreaGap({
          vendorTypes: [vendorType],
          serviceRadiusMiles: useCustomRadius ? (parseInt(customRadius) || 0) : serviceRadius,
          additionalCities,
        }),
        ...getVendorSpecificFields(),
        reelUrl,
        hasReel: true,
        imageUrls,
        imageCount: imageUrls.length,
        status: 'pending',
        submittedAt: serverTimestamp(),
      });

      navigate('/vendor/dashboard');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else {
        setError('Something went wrong. Please try again.');
      }
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  const ChipSection = ({ title, options, selected, onToggle }) => (
    <div style={{ marginBottom: '24px' }}>
      <label style={styles.label}>{title}</label>
      <div style={styles.chipRow}>
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onToggle(opt)}
            style={{
              ...styles.chip,
              ...(selected.includes(opt) ? styles.chipSelected : {}),
            }}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <nav style={styles.nav}>
        <Link to="/" style={styles.logo}>PLND</Link>
      </nav>

      <div style={styles.content}>
        <div style={styles.card}>
          <h1 style={styles.title}>Vendor Registration</h1>
          <p style={styles.subtitle}>List your business on PLND in just a few steps</p>

          {/* Progress bar */}
          <div style={styles.progressContainer}>
            {STEP_LABELS.map((label, i) => (
              <div key={i} style={styles.progressStep}>
                <div style={{
                  ...styles.progressDot,
                  backgroundColor: i <= step ? '#000000' : '#e0e0e0',
                  color: i <= step ? 'white' : '#999',
                }}>
                  {i + 1}
                </div>
                <span style={{
                  ...styles.progressLabel,
                  color: i <= step ? '#000000' : '#999',
                  fontWeight: i === step ? '700' : '500',
                }}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          {error && <p style={styles.error}>{error}</p>}

          {/* Step 0: Account */}
          {step === 0 && (
            <div>
              <div style={styles.field}>
                <label style={styles.label}>Company Name *</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  style={styles.input}
                  placeholder="Your Business Name"
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Vendor Type *</label>
                <select
                  value={vendorType}
                  onChange={(e) => setVendorType(e.target.value)}
                  style={styles.input}
                >
                  <option value="">Select a type...</option>
                  {ALL_VENDOR_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                  placeholder="your@email.com"
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={styles.input}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Confirm Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={styles.input}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>How did you hear about PLND? *</label>
                <select
                  value={heardAboutUs}
                  onChange={(e) => setHeardAboutUs(e.target.value)}
                  style={styles.input}
                >
                  <option value="">Select an option...</option>
                  {HEARD_ABOUT_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              {heardAboutUs === 'Other' && (
                <div style={styles.field}>
                  <label style={styles.label}>Please specify *</label>
                  <input
                    type="text"
                    value={heardAboutUsOther}
                    onChange={(e) => setHeardAboutUsOther(e.target.value)}
                    style={styles.input}
                    placeholder="Tell us how you heard about us"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 1: Business Info */}
          {step === 1 && (
            <div>
              <div style={styles.field}>
                <label style={styles.label}>Phone *</label>
                <input
                  type="tel"
                  value={formatPhoneInput(phone)}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  style={styles.input}
                  placeholder="(555) 555-5555"
                />
              </div>

              <div style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={phonePublic}
                  onChange={(e) => setPhonePublic(e.target.checked)}
                  id="phonePublic"
                />
                <label htmlFor="phonePublic" style={styles.checkboxLabel}>
                  Show phone number publicly
                </label>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Contact Name *</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  style={styles.input}
                  placeholder="Primary contact name"
                />
              </div>

              <div style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={contactNamePublic}
                  onChange={(e) => setContactNamePublic(e.target.checked)}
                  id="contactPublic"
                />
                <label htmlFor="contactPublic" style={styles.checkboxLabel}>
                  Show contact name publicly
                </label>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Website (optional)</label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  style={styles.input}
                  placeholder="https://yourbusiness.com"
                />
              </div>

              <h3 style={styles.sectionTitle}>Business Address</h3>

              <div style={styles.field}>
                <label style={styles.label}>Address Line 1 *</label>
                <input
                  type="text"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  style={styles.input}
                  placeholder="123 Main St"
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Address Line 2 (optional)</label>
                <input
                  type="text"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  style={styles.input}
                  placeholder="Suite 100"
                />
              </div>

              <div style={styles.row}>
                <div style={{ ...styles.field, flex: 2 }}>
                  <label style={styles.label}>City *</label>
                  <CityAutocomplete
                    value={city}
                    onChange={setCity}
                    onCitySelected={(cityName, matchedState) => {
                      setCity(cityName);
                      if (!state) setState(matchedState);
                    }}
                    stateFilter={state}
                    style={styles.input}
                  />
                </div>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label}>State *</label>
                  <select
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    style={styles.input}
                  >
                    <option value="">--</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label}>Zipcode *</label>
                  <input
                    type="text"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>

              <h3 style={styles.sectionTitle}>About Your Business</h3>

              <div style={styles.field}>
                <label style={styles.label}>What Sets You Apart - one or two word descriptors *</label>
                <input
                  type="text"
                  value={diff1}
                  onChange={(e) => setDiff1(e.target.value.slice(0, 20))}
                  style={{ ...styles.input, marginBottom: '8px' }}
                  placeholder="Differentiator 1"
                  maxLength={20}
                />
                <input
                  type="text"
                  value={diff2}
                  onChange={(e) => setDiff2(e.target.value.slice(0, 20))}
                  style={{ ...styles.input, marginBottom: '8px' }}
                  placeholder="Differentiator 2"
                  maxLength={20}
                />
                <input
                  type="text"
                  value={diff3}
                  onChange={(e) => setDiff3(e.target.value.slice(0, 20))}
                  style={styles.input}
                  placeholder="Differentiator 3"
                  maxLength={20}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Brief Intro (160 chars max) *</label>
                <textarea
                  value={briefIntro}
                  onChange={(e) => setBriefIntro(e.target.value.slice(0, 160))}
                  style={{ ...styles.input, minHeight: '70px', resize: 'vertical' }}
                  maxLength={160}
                />
                <span style={styles.charCount}>{briefIntro.length}/160</span>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Bio / Description *</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  style={{ ...styles.input, minHeight: '100px', resize: 'vertical' }}
                />
              </div>

              {SHOW_INSURANCE_FEATURE && (
                <div style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={hasInsurance}
                    onChange={(e) => setHasInsurance(e.target.checked)}
                    id="hasInsurance"
                  />
                  <label htmlFor="hasInsurance" style={styles.checkboxLabel}>
                    I have business insurance
                  </label>
                </div>
              )}

              <div style={styles.field}>
                <label style={styles.label}>Year Started in Business (optional)</label>
                <div style={styles.row}>
                  <div style={{ flex: 2 }}>
                    <select
                      value={experienceYear}
                      onChange={(e) => setExperienceYear(e.target.value)}
                      style={styles.input}
                    >
                      <option value="">Not specified</option>
                      {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  {experienceYear && (
                    <div style={{ flex: 3, display: 'flex', alignItems: 'center', backgroundColor: '#F5F5EF', borderRadius: '10px', padding: '0 16px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#000000' }}>
                        {new Date().getFullYear() - parseInt(experienceYear)} years in business
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <ChipSection
                title="Languages Spoken (optional)"
                options={ALL_LANGUAGES}
                selected={languages}
                onToggle={(v) => toggleInArray(v, languages, setLanguages)}
              />
              {languages.includes('Other') && (
                <div style={styles.field}>
                  <input
                    type="text"
                    value={otherLanguage}
                    onChange={(e) => setOtherLanguage(e.target.value.slice(0, 15))}
                    style={styles.input}
                    placeholder="Specify other language"
                    maxLength={15}
                  />
                </div>
              )}

              <ChipSection
                title="Diversity Tags (optional)"
                options={DIVERSITY_TAGS}
                selected={diversityTags}
                onToggle={(v) => toggleInArray(v, diversityTags, setDiversityTags)}
              />

              <h3 style={styles.sectionTitle}>Social Media (optional)</h3>

              <div style={styles.field}>
                <label style={styles.label}>Instagram</label>
                <input type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)} style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Facebook</label>
                <input type="text" value={facebook} onChange={(e) => setFacebook(e.target.value)} style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>TikTok</label>
                <input type="text" value={tiktok} onChange={(e) => setTiktok(e.target.value)} style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Pinterest</label>
                <input type="text" value={pinterest} onChange={(e) => setPinterest(e.target.value)} style={styles.input} />
              </div>
            </div>
          )}

          {/* Step 2: Service Area */}
          {step === 2 && (
            <div>
              {(vendorType === 'Venue' || vendorType === 'Wedding Attire') ? (
                <>
                  <div style={styles.addressDisplay}>
                    <span style={styles.addressLabel}>Business Address</span>
                    <p style={styles.addressText}>
                      {addressLine1}{addressLine2 ? `, ${addressLine2}` : ''}<br />
                      {city}, {state} {zip}
                    </p>
                  </div>

                  <p style={styles.helperText}>
                    Your exact business address will be used as your location. No service radius needed.
                  </p>

                  {geocoding && (
                    <p style={styles.helperText}>Locating address on map...</p>
                  )}

                  {!geocoding && geocodeFailed && (
                    <div style={styles.warningBox}>
                      ⚠️ We couldn't verify this address. Please double-check it's correct before continuing — your exact location may not display accurately to couples.
                    </div>
                  )}

                  {mapCoords && (
                    <div style={styles.mapContainer}>
                      <MapContainer
                        center={[mapCoords.lat, mapCoords.lng]}
                        zoom={13}
                        style={{ height: '280px', width: '100%', borderRadius: '12px' }}
                        key={`${mapCoords.lat}-${mapCoords.lng}-exact`}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        />
                        <Marker position={[mapCoords.lat, mapCoords.lng]} />
                      </MapContainer>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={styles.addressDisplay}>
                    <span style={styles.addressLabel}>Business Address</span>
                    <p style={styles.addressText}>
                      {addressLine1}{addressLine2 ? `, ${addressLine2}` : ''}<br />
                      {city}, {state} {zip}
                    </p>
                  </div>

                  {geocoding && (
                    <p style={styles.helperText}>Locating address on map...</p>
                  )}

                  {!geocoding && geocodeFailed && (
                    <div style={styles.warningBox}>
                      ⚠️ We couldn't verify this address. Please double-check it's correct before continuing — your service area may not display accurately to couples.
                    </div>
                  )}

                  {mapCoords && (
                    <div style={styles.mapContainer}>
                      <MapContainer
                        center={[mapCoords.lat, mapCoords.lng]}
                        zoom={getZoomForRadius(useCustomRadius ? (parseInt(customRadius) || 25) : serviceRadius)}
                        style={{ height: '280px', width: '100%', borderRadius: '12px' }}
                        key={`${mapCoords.lat}-${mapCoords.lng}-${useCustomRadius ? customRadius : serviceRadius}`}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        />
                        <Marker position={[mapCoords.lat, mapCoords.lng]} />
                        <Circle
                          center={[mapCoords.lat, mapCoords.lng]}
                          radius={(useCustomRadius ? (parseInt(customRadius) || 0) : serviceRadius) * 1609.34}
                          pathOptions={{ color: '#000000', fillColor: '#000000', fillOpacity: 0.1 }}
                        />
                        {cityMarkers.map((m, i) => (
                          <Marker key={i} position={[m.lat, m.lng]} />
                        ))}
                      </MapContainer>
                    </div>
                  )}

                  <h3 style={styles.sectionTitle}>Service Radius</h3>
                  <p style={styles.helperText}>How far are you willing to travel from your business address?</p>

                  <div style={styles.chipRow}>
                    {RADIUS_OPTIONS.map((miles) => (
                      <button
                        key={miles}
                        type="button"
                        onClick={() => {
                          setServiceRadius(miles);
                          setUseCustomRadius(false);
                          setCustomRadius('');
                        }}
                        style={{
                          ...styles.chip,
                          ...(!useCustomRadius && serviceRadius === miles ? styles.chipSelected : {}),
                        }}
                      >
                        {miles} miles
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setUseCustomRadius(true)}
                      style={{
                        ...styles.chip,
                        ...(useCustomRadius ? styles.chipSelected : {}),
                      }}
                    >
                      Custom
                    </button>
                  </div>

                  {useCustomRadius && (
                    <div style={styles.field}>
                      <input
                        type="number"
                        value={customRadius}
                        onChange={(e) => setCustomRadius(e.target.value)}
                        style={styles.input}
                        placeholder="Enter custom radius (miles)"
                      />
                    </div>
                  )}

                  <h3 style={styles.sectionTitle}>Additional Cities (optional)</h3>
                  <p style={styles.helperText}>Add other cities you serve outside your radius</p>

                  <div style={{ ...styles.row, width: '100%' }}>
                    <CityAutocomplete
                      value={newCity}
                      onChange={setNewCity}
                      placeholder="City, State (e.g. San Diego, CA)"
                      style={{ ...styles.input, flex: 1, minWidth: 0, boxSizing: 'border-box' }}
                    />
                    <button type="button" onClick={addCity} style={styles.addButton}>
                      Add
                    </button>
                  </div>

                  {additionalCities.length > 0 && (
                    <div style={styles.cityChipRow}>
                      {additionalCities.map((c) => (
                        <div key={c} style={styles.cityChip}>
                          <span>{c}</span>
                          <button
                            type="button"
                            onClick={() => removeCity(c)}
                            style={styles.cityChipRemove}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3: Vendor Specific */}
          {step === 3 && (
            <div>
              {(vendorType === 'Photography' || vendorType === 'Videography') && (
                <VendorTypeForm schema={photographySchema} values={photographyValues} onChange={handlePhotographyChange} styles={styles} />
              )}

              {vendorType === 'Venue' && (
                <VendorTypeForm schema={venueSchema} values={venueValues} onChange={handleVenueChange} styles={styles} />
              )}

              {vendorType === 'Photo Booths' && (
                <VendorTypeForm schema={photoBoothsSchema} values={photoBoothsValues} onChange={handlePhotoBoothsChange} styles={styles} />
              )}

              {vendorType === 'Cakes & Bakeries' && (
                <VendorTypeForm schema={cakesSchema} values={cakesValues} onChange={handleCakesChange} styles={styles} />
              )}

              {vendorType === 'Planners' && (
                <VendorTypeForm schema={plannersSchema} values={plannerValues} onChange={handlePlannerChange} styles={styles} />
              )}

              {vendorType === 'Caterers' && (
                <VendorTypeForm schema={caterersSchema} values={caterersValues} onChange={handleCaterersChange} styles={styles} />
              )}

              {vendorType === 'Hair & Makeup' && (
                <VendorTypeForm schema={hairMakeupSchema} values={hairMakeupValues} onChange={handleHairMakeupChange} styles={styles} />
              )}

              {(vendorType === 'DJs' || vendorType === 'Bands' || vendorType === 'Musicians') && (
                <VendorTypeForm schema={djBandMusicianSchema} values={djBandValues} onChange={handleDjBandChange} styles={styles} />
              )}

              {vendorType === 'Florists' && (
                <VendorTypeForm schema={floristsSchema} values={floristValues} onChange={handleFloristChange} styles={styles} />
              )}

              {vendorType === 'Officiants' && (
                <VendorTypeForm schema={officiantsSchema} values={officiantValues} onChange={handleOfficiantChange} styles={styles} />
              )}

              {vendorType === 'Wedding Attire' && (
                <VendorTypeForm schema={weddingAttireSchema} values={weddingAttireValues} onChange={handleWeddingAttireChange} styles={styles} />
              )}

              {vendorType === 'Transportation' && (
                <VendorTypeForm schema={transportationSchema} values={transportationValues} onChange={handleTransportationChange} styles={styles} />
              )}

              {vendorType === 'Rentals/Décor/Lighting' && (
                <VendorTypeForm schema={rentalsSchema} values={rentalsValues} onChange={handleRentalsChange} styles={styles} />
              )}

              {vendorType === 'Invitations & Stationery' && (
                <VendorTypeForm schema={invitationsSchema} values={invitationsValues} onChange={handleInvitationsChange} styles={styles} />
              )}

              {vendorType !== 'Photography' && vendorType !== 'Videography' && vendorType !== 'Venue' && vendorType !== 'Photo Booths' && vendorType !== 'Cakes & Bakeries' && vendorType !== 'Planners' && vendorType !== 'Caterers' && vendorType !== 'Hair & Makeup' && vendorType !== 'DJs' && vendorType !== 'Bands' && vendorType !== 'Musicians' && vendorType !== 'Florists' && vendorType !== 'Officiants' && vendorType !== 'Wedding Attire' && vendorType !== 'Transportation' && vendorType !== 'Rentals/Décor/Lighting' && vendorType !== 'Invitations & Stationery' && (
                <div style={styles.placeholderBox}>
                  <p>Detailed questions for {vendorType || 'this vendor type'} are coming soon. You can skip this step for now.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Media */}
          {step === 4 && (
            <div>
              <h3 style={styles.sectionTitle}>Intro Reel *</h3>
              <p style={styles.helperText}>Upload a short video reel (max 60 seconds, required)</p>

              {reelPreview ? (
                <div style={styles.mediaPreviewBox}>
                  <video src={reelPreview} controls style={styles.videoPreview} />
                  <label style={styles.replaceLabel}>
                    Replace Reel
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleReelSelect}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              ) : (
                <label style={styles.uploadBox}>
                  <span style={styles.uploadIcon}>🎥</span>
                  <span>Tap to select your intro reel</span>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleReelSelect}
                    style={{ display: 'none' }}
                  />
                </label>
              )}

              <h3 style={{ ...styles.sectionTitle, marginTop: '32px' }}>
                Gallery Images * ({imageFiles.length}/9)
              </h3>
              <p style={styles.helperText}>Upload 1–9 images of your work</p>

              <div style={styles.imageGrid}>
                {imagePreviews.map((src, i) => (
                  <div key={i} style={styles.imageTile}>
                    <img src={src} alt={`Gallery ${i + 1}`} style={styles.imageTileImg} />
                    <button
                      type="button"
                      onClick={() => setEditingCropIndex(i)}
                      style={styles.imageTileEdit}
                    >
                      <span className="material-icons" style={{ fontSize: '14px' }}>crop</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      style={styles.imageTileRemove}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {imageFiles.length < 9 && (
                  <label style={styles.imageTileAdd}>
                    <span style={{ fontSize: '24px' }}>+</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImagesSelect}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}
              </div>

              {uploadProgress && (
                <p style={styles.uploadProgressText}>{uploadProgress}</p>
              )}

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '16px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={mediaRightsConfirmed}
                  onChange={() => {
                    if (!mediaRightsConfirmed) {
                      setShowMediaRightsModal(true);
                    } else {
                      setMediaRightsConfirmed(false);
                    }
                  }}
                  style={{ marginTop: '3px' }}
                />
                <span style={{ fontSize: '13px', color: '#333' }}>
                  I confirm I have the necessary rights to the reel and images uploaded above. *
                </span>
              </label>

              {showMediaRightsModal && (
                <MediaRightsConfirmationModal
                  onCancel={() => setShowMediaRightsModal(false)}
                  onConfirm={() => {
                    setMediaRightsConfirmed(true);
                    setShowMediaRightsModal(false);
                  }}
                />
              )}
            </div>
          )}

          {step === 0 && (
            <p style={{ fontSize: '12px', color: '#888', textAlign: 'center', marginBottom: '16px' }}>
              By creating an account you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#000000', fontWeight: '600' }}>Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#000000', fontWeight: '600' }}>Privacy Policy</a>.
            </p>
          )}

          {/* Navigation buttons */}
          <div style={styles.buttonRow}>
            {step > 0 && (
              <button onClick={handleBack} style={styles.backButton} disabled={submitting}>
                Back
              </button>
            )}
            {step < STEP_LABELS.length - 1 ? (
              <button onClick={handleNext} style={styles.nextButton}>
                Continue
              </button>
            ) : (
              <button onClick={handleSubmit} style={styles.nextButton} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            )}
          </div>

          <p style={styles.footer}>
            Already have an account?{' '}
            <Link to="/vendor/login" style={styles.link}>Sign in</Link>
          </p>
        </div>
      </div>
      {croppingFile && (
        <ImageCropModal
          imageSrc={croppingFile.previewUrl}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      )}
      {editingCropIndex !== null && (
        <ImageCropModal
          imageSrc={imagePreviews[editingCropIndex]}
          initialCrop={
            imageCrops[editingCropIndex]
              ? { x: imageCrops[editingCropIndex].panX ?? 0, y: imageCrops[editingCropIndex].panY ?? 0 }
              : undefined
          }
          initialZoom={imageCrops[editingCropIndex]?.zoom ?? 1}
          onCancel={() => setEditingCropIndex(null)}
          onConfirm={handleEditCropConfirm}
        />
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#F5F5EF', fontFamily: "'Inter', sans-serif" },
  nav: { padding: '20px 48px', borderBottom: '1px solid #e8e8e0', backgroundColor: '#F5F5EF' },
  logo: { fontSize: '28px', fontWeight: '800', color: '#000000', letterSpacing: '4px', textDecoration: 'none' },
  content: { display: 'flex', justifyContent: 'center', padding: '48px 24px' },
  card: { backgroundColor: 'white', borderRadius: '20px', padding: '48px', width: '100%', maxWidth: '560px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' },
  title: { fontSize: '28px', fontWeight: '800', color: '#000000', marginBottom: '8px', textAlign: 'center' },
  subtitle: { fontSize: '15px', color: '#888', textAlign: 'center', marginBottom: '32px' },
  progressContainer: { display: 'flex', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '8px' },
  progressStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1 },
  progressDot: { width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700' },
  progressLabel: { fontSize: '11px', textAlign: 'center' },
  field: { marginBottom: '20px' },
  row: { display: 'flex', gap: '12px' },
  sectionTitle: { fontSize: '17px', fontWeight: '700', color: '#000000', marginTop: '8px', marginBottom: '16px' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' },
  checkboxLabel: { fontSize: '14px', color: '#444' },
  charCount: { fontSize: '11px', color: '#999', display: 'block', textAlign: 'right', marginTop: '4px' },
  helperText: { fontSize: '13px', color: '#888', marginBottom: '16px', marginTop: '-8px' },
  addressDisplay: { backgroundColor: '#F5F5EF', borderRadius: '12px', padding: '16px', marginBottom: '20px' },
  addressLabel: { fontSize: '12px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  addressText: { fontSize: '15px', color: '#2D2D2D', marginTop: '6px', lineHeight: 1.5 },
  mapContainer: { marginBottom: '24px', borderRadius: '12px', overflow: 'hidden' },
  chipRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '24px' },
  chip: { padding: '10px 18px', borderRadius: '20px', border: '1.5px solid #e0e0e0', backgroundColor: 'white', color: '#666', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  chipSelected: { backgroundColor: '#000000', borderColor: '#000000', color: 'white' },
  addButton: { padding: '14px 20px', backgroundColor: '#000000', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  cityChipRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' },
  cityChip: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f0f0f0', borderRadius: '20px', fontSize: '13px', color: '#444' },
  cityChipRemove: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#888', padding: 0, lineHeight: 1 },
  uploadBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '40px', backgroundColor: '#F5F5EF', borderRadius: '12px', border: '1.5px dashed #ccc', cursor: 'pointer', color: '#666', fontSize: '14px' },
  uploadIcon: { fontSize: '32px' },
  mediaPreviewBox: { display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' },
  videoPreview: { width: '100%', maxHeight: '300px', borderRadius: '12px', backgroundColor: '#000' },
  replaceLabel: { padding: '10px 20px', border: '1.5px solid #000000', color: '#000000', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  imageGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' },
  imageTile: { position: 'relative', aspectRatio: TARGET_ASPECT_RATIO, borderRadius: '8px', overflow: 'hidden' },
  imageTileImg: { width: '100%', height: '100%', objectFit: 'cover' },
  imageTileRemove: { position: 'absolute', top: '4px', right: '4px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '14px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  imageTileEdit: { position: 'absolute', bottom: '4px', left: '4px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  imageTileAdd: { aspectRatio: TARGET_ASPECT_RATIO, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F5EF', borderRadius: '8px', border: '1.5px dashed #ccc', cursor: 'pointer', color: '#999' },
  uploadProgressText: { textAlign: 'center', color: '#000000', fontSize: '14px', fontWeight: '600', marginTop: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '8px' },
  input: { width: '100%', padding: '14px 16px', borderRadius: '10px', border: '1.5px solid #e0e0e0', fontSize: '15px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white' },
  error: { color: '#e53935', fontSize: '14px', marginBottom: '16px', textAlign: 'center' },
  placeholderBox: { padding: '40px', textAlign: 'center', color: '#888', backgroundColor: '#F5F5EF', borderRadius: '12px' },
  nestedBox: { backgroundColor: '#F5F5EF', borderRadius: '12px', padding: '16px', marginBottom: '24px' },
  groupShaded: { backgroundColor: '#F5F5EF', borderRadius: '12px', padding: '16px 16px 0 16px', marginBottom: '24px', border: '1px solid rgba(0,0,0,0.2)' },
  groupNested: { backgroundColor: 'white', borderRadius: '12px', padding: '16px 16px 0 16px', marginBottom: '24px', border: '1px solid rgba(0,0,0,0.2)' },
  groupTitle: { fontSize: '16px', fontWeight: '700', color: '#000000', marginBottom: '12px' },
  priceInputWrapper: { position: 'relative' },
  priceInputPrefix: { position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: '15px', pointerEvents: 'none' },
  priceInput: { paddingLeft: '28px' },
  warningBox: { backgroundColor: '#fff8e1', border: '1px solid #ffc107', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px', color: '#856404', lineHeight: 1.5 },
  buttonRow: { display: 'flex', gap: '12px', marginTop: '8px' },
  backButton: { flex: 1, padding: '16px', backgroundColor: 'white', color: '#000000', border: '1.5px solid #000000', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer' },
  nextButton: { flex: 2, padding: '16px', backgroundColor: '#000000', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer' },
  footer: { textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#888' },
  link: { color: '#000000', fontWeight: '600', textDecoration: 'none' },
};